import { useAxios, useSubjectId } from '@healthblocks-io/core'
import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Outlet, useNavigate, useOutlet, useParams } from 'react-router-dom'
import { atom, useRecoilState } from 'recoil'
import styled from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { AppHeader } from '@/@bothrs-ui/web/components/navigation/header/AppHeader'
import { TabBar } from '@/@bothrs-ui/web/components/tab-bar/TabBar'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { NativePlatform, WebPlatform } from '@/capacitor/components/native-platform/NativePlatform'
import { breakpoints } from '@/config/layout'
import { globalDefinitions } from '@/defined-translations'
import { ContentArea } from '@/layout/components/content-area/ContentArea'
import { ContentGrid } from '@/layout/components/content-layout/ContentLayout.styled'
import { mixpanel } from '@/mixpanel'
import { shouldShowBanner } from '@/modules/shared/components/environment-banner/EnvironmentBanner'
import { ScreenType, useScreenType } from '@/modules/shared/hooks/useScreenType'
import { scrollToId } from '@/modules/timeline/components/timeline/helpers/animation'
import { useLoggedInForMandatee } from '@/modules/trust/hooks/useLoggedInForMandatee'
import { MessageList } from '../components/message-list/MessageList'
import { type Message, MessageReadStatus, type MessageResponse } from '../types/message.types'

export enum MessageCenterTab {
  Read = 'read',
  Unread = 'unread',
}

const ITEMS_PER_PAGE = 20

type MessageState = {
  messages: Message[]
  selectedMessageId?: string
  currentPage: number
  hasMore: boolean
  status: 'initial' | 'loading' | 'done'
}

export const messageStateAtom = atom<MessageState>({
  key: 'messageStateAtom',
  default: {
    messages: [],
    currentPage: 0,
    hasMore: true,
    status: 'initial',
  },
})

export const MessageRootPage = () => {
  const isInitializedRef = useRef(false)

  // State
  // ------------------------------------------------------------------------- /
  const [activeTab, setActiveTab] = useState<MessageCenterTab>(MessageCenterTab.Unread)
  const [messageState, setMessageState] = useRecoilState(messageStateAtom)

  // Hooks
  // ------------------------------------------------------------------------- /
  const outlet = useOutlet()
  const axios = useAxios()
  const navigate = useNavigate()
  const subjectId = useSubjectId()
  const isLoggedInForMandatee = useLoggedInForMandatee()
  const { id: routeParameterMessageId } = useParams()

  const screenType = useScreenType()

  // Effects
  // ------------------------------------------------------------------------- /

  useEffect(() => {
    if (!screenType || isInitializedRef.current) return

    const initializeMessageList = async () => {
      // Prioritize route param message ID over state
      const messageIdToLoad = routeParameterMessageId || messageState.selectedMessageId

      if (messageIdToLoad) {
        // If we have a message ID (from route or state), fetch its details and set up the view
        try {
          const message = await axios.get<Message>(`/message/${messageIdToLoad}`)
          const status =
            message.data.readStatus === MessageReadStatus.Unread ? MessageCenterTab.Unread : MessageCenterTab.Read
          setActiveTab(status)
          await loadMessages({
            pageToFetch: 0,
            tab: status,
            messageIdToSelect: messageIdToLoad,
          })
        } catch (error) {
          console.error('Failed to fetch message:', error)
          setActiveTab(MessageCenterTab.Unread)
          // If the selected message can't be fetched (e.g. expired deep link), still load the unread list.
          await loadMessages({ pageToFetch: 0, tab: MessageCenterTab.Unread })
        }
      } else {
        // Otherwise just load the unread messages
        await loadMessages({ pageToFetch: 0, tab: MessageCenterTab.Unread })
      }
    }

    initializeMessageList()
    isInitializedRef.current = true
  }, [routeParameterMessageId, screenType])

  useEffect(() => {
    return () => {
      setMessageState((previous) => ({
        ...previous,
        status: 'initial',
        selectedMessageId: undefined,
        messages: [],
      }))
      isInitializedRef.current = false
    }
  }, [])

  // Methods
  // ------------------------------------------------------------------------- /
  /**
   * Handles first message selection logic for desktop
   * @param messages - The messages to select from
   * @param pageToFetch - Current page being fetched
   * @returns The selected message ID if any
   */
  const handleFirstMessageSelection = useCallback(
    (messages: Message[], pageToFetch: number) => {
      // Web UX: don't auto-open the first message by default.
      // We only navigate to a specific message when the user clicks one (or when a deep link provides `:id`).
      // Skip navigation when a route parameter message ID exists — the detail panel
      // should stay visible (e.g. showing DeeplinkNotFound for an invalid deeplink).
      if (pageToFetch === 0 && screenType !== ScreenType.Mobile && !routeParameterMessageId) {
        navigate('/message')
      }

      return undefined
    },
    [screenType, navigate, routeParameterMessageId],
  )

  /**
   * Loads messages for a given page and tab
   *  (If you are on the unread tab, we select the first message and navigate to it)
   * @param pageToFetch - The page to fetch
   * @param tab - The tab to fetch messages for
   * @param messageIdToSelect - Optional specific message ID to find and select
   * @returns The messages
   */
  const loadMessages = useCallback(
    async ({
      pageToFetch,
      tab,
      limit = ITEMS_PER_PAGE,
      messageIdToSelect,
    }: {
      pageToFetch: number
      tab: MessageCenterTab
      limit?: number
      messageIdToSelect?: string
    }): Promise<Message[] | undefined> => {
      if (messageState.status === 'loading' || !screenType) return

      setMessageState((previous) => ({ ...previous, status: 'loading' }))

      const statusQuery = tab === MessageCenterTab.Unread ? 'unread' : 'read'
      const endpoint = `/message?readStatus=${statusQuery}&page=${pageToFetch}&limit=${limit}&subjectId=${subjectId}`

      try {
        const response = await axios.get<MessageResponse>(endpoint)
        const { data: newMessages, page, total, limit } = response.data

        // If we're looking for a specific message, check if it's in this batch
        if (messageIdToSelect) {
          const foundMessageIndex = newMessages.findIndex((message) => message.id === messageIdToSelect)

          if (foundMessageIndex === -1) {
            // Message not found in this batch, check if we have more pages
            const hasMorePages = page * limit + newMessages.length < total

            if (hasMorePages) {
              // Accumulate messages and recursively fetch next page
              setMessageState((previous) => ({
                ...previous,
                messages: pageToFetch === 0 ? newMessages : [...previous.messages, ...newMessages],
                currentPage: page,
                selectedMessageId: messageIdToSelect,
                hasMore: hasMorePages,
                status: 'done',
              }))

              return loadMessages({
                pageToFetch: page + 1,
                tab,
                messageIdToSelect,
                limit: 100,
              })
            } else {
              // No more pages and message not found, apply normal first message selection logic
              const defaultSelectedMessageId = handleFirstMessageSelection(newMessages, pageToFetch)

              setMessageState((previous) => ({
                ...previous,
                messages:
                  pageToFetch === 0 && tab === MessageCenterTab.Unread
                    ? newMessages.map((message) =>
                        message.id === defaultSelectedMessageId
                          ? { ...message, readStatus: MessageReadStatus.Read }
                          : message,
                      )
                    : pageToFetch === 0
                      ? newMessages
                      : [...previous.messages, ...newMessages],
                currentPage: page,
                selectedMessageId: defaultSelectedMessageId,
                hasMore: false,
                status: 'done',
              }))

              return newMessages
            }
          } else {
            // Message found! Update state and navigate
            const foundMessage = newMessages[foundMessageIndex]

            // Update state with all messages up to this point
            setMessageState((previous) => ({
              ...previous,
              messages:
                pageToFetch === 0 && tab === MessageCenterTab.Unread
                  ? newMessages.map((message) =>
                      message.id === messageIdToSelect ? { ...message, readStatus: MessageReadStatus.Read } : message,
                    )
                  : pageToFetch === 0
                    ? newMessages
                    : [...previous.messages, ...newMessages],
              currentPage: page,
              selectedMessageId: messageIdToSelect,
              hasMore: page * limit + newMessages.length < total,
              status: 'done',
            }))

            // Navigate to the message
            navigate(`/message/${messageIdToSelect}`)

            // Scroll to the message on desktop
            if (screenType !== ScreenType.Mobile) {
              setTimeout(() => {
                scrollToId(`message_${messageIdToSelect}`)
              })
            }

            // Add tracking
            mixpanel.track(activeTab === MessageCenterTab.Unread ? 'message_unread_click' : 'message_read_click', {
              origin,
              message_id: messageIdToSelect,
              message_type: foundMessage.type ?? 'unknown',
              is_mandate: isLoggedInForMandatee,
            })

            return newMessages
          }
        }

        // Original logic for when no specific message is being searched for
        const defaultSelectedMessageId = handleFirstMessageSelection(newMessages, pageToFetch)

        setMessageState((previous) => ({
          ...previous,
          messages:
            pageToFetch === 0 && tab === MessageCenterTab.Unread
              ? newMessages.map((message) =>
                  message.id === defaultSelectedMessageId
                    ? { ...message, readStatus: MessageReadStatus.Read }
                    : message,
                )
              : pageToFetch === 0
                ? newMessages
                : [...previous.messages, ...newMessages],
          currentPage: page,
          selectedMessageId: defaultSelectedMessageId,
          hasMore: page * limit + newMessages.length < total,
          status: 'done',
        }))

        return newMessages
      } catch (error) {
        console.error('Failed to fetch messages:', error)
        setMessageState((previous) => ({
          ...previous,
          hasMore: false,
          status: 'done',
        }))
      }
    },
    [
      axios,
      subjectId,
      messageState.status,
      screenType,
      navigate,
      setMessageState,
      activeTab,
      handleFirstMessageSelection,
    ],
  )

  /**
   * Handles tab click
   * @param tab - The tab to switch to
   */
  const handleTabClick = useCallback(
    (tab: MessageCenterTab) => {
      mixpanel.track('message_tab_switch', {
        origin,
        from_tab: activeTab,
        to_tab: tab,
        is_mandate: isLoggedInForMandatee,
      })

      setActiveTab(tab)
      setMessageState((previous) => ({
        ...previous,
        messages: [],
        currentPage: 0,
        hasMore: true,
      }))

      // On desktop, close the detail view when switching tabs to avoid mismatched detail vs list.
      if (screenType !== ScreenType.Mobile) {
        navigate('/message')
      }

      loadMessages({ pageToFetch: 0, tab })
    },
    [activeTab, loadMessages, navigate, screenType, setMessageState],
  )

  /**
   * Handles end reached
   */
  const handleEndReached = () => {
    if (messageState.hasMore && messageState.status === 'done') {
      loadMessages({
        pageToFetch: messageState.currentPage + 1,
        tab: activeTab,
      })
    }
  }

  /**
   * Handles message selection and navigation:
   * - Mobile: Navigates to message detail
   * - Desktop: Updates message read status optimistically
   *
   * Optimistic updates/animation that removes the message:
   * - Skips if message is already read
   * - Animates out messages with pending updates
   * - Tracks messages for animation after update completes
   */
  const handleMessageSelected = (messageId: string) => {
    const message = messageState.messages.find((message) => message.id === messageId)

    mixpanel.track(activeTab === MessageCenterTab.Unread ? 'message_unread_click' : 'message_read_click', {
      origin,
      message_id: messageId,
      message_type: message?.type ?? 'unknown',
      is_mandate: isLoggedInForMandatee,
    })

    // Select message and mark as read locally (keep it in the current list)
    // Server-side update happens in `MessageDetailPage`.
    setMessageState((previous) => ({
      ...previous,
      selectedMessageId: messageId,
      messages:
        activeTab === MessageCenterTab.Unread
          ? previous.messages.map((m) => (m.id === messageId ? { ...m, readStatus: MessageReadStatus.Read } : m))
          : previous.messages,
    }))

    navigate(`/message/${messageId}`)
  }

  // Template variables
  // ------------------------------------------------------------------------- /
  const tabs = useMemo(
    () => [
      {
        id: MessageCenterTab.Unread,
        title: i18n._(globalDefinitions.message.unread),
        onClick: () => handleTabClick(MessageCenterTab.Unread),
      },
      {
        id: MessageCenterTab.Read,
        title: i18n._(globalDefinitions.message.read),
        onClick: () => handleTabClick(MessageCenterTab.Read),
      },
    ],
    [handleTabClick],
  )

  // Render
  // ------------------------------------------------------------------------- /
  return (
    <StContentGrid>
      {/* Main content */}
      <ContentArea gridArea='content'>
        {/* Web platform */}
        <WebPlatform>
          <StDesktopMessageList>
            <StWebTabbar activeTabId={activeTab} tabs={tabs} variant='underline' />

            <MessageList
              messages={messageState.messages}
              selectedMessageId={messageState.selectedMessageId}
              activeTab={activeTab}
              hasMore={messageState.hasMore}
              isLoading={messageState.status !== 'done'}
              onEndReached={handleEndReached}
              onMessageSelected={handleMessageSelected}
            />
          </StDesktopMessageList>
        </WebPlatform>

        {/* Native platform */}
        <NativePlatform>
          <StMobileMessageList>
            <StMobileHeaderContainer>
              <AppHeader
                enableNotifications
                profileButtonEnabled
                centered={false}
                title={i18n._(globalDefinitions.message.title)}
              />
            </StMobileHeaderContainer>

            <TabBar variant='underline' activeTabId={activeTab} tabs={tabs} />

            <MessageList
              messages={messageState.messages}
              selectedMessageId={messageState.selectedMessageId}
              activeTab={activeTab}
              hasMore={messageState.hasMore}
              isLoading={messageState.status !== 'done'}
              onEndReached={handleEndReached}
              onMessageSelected={handleMessageSelected}
            />
          </StMobileMessageList>
        </NativePlatform>
      </ContentArea>

      {/* Detail content */}
      {outlet ? (
        <ContentArea gridArea='content-detail'>
          <Outlet />
        </ContentArea>
      ) : screenType === ScreenType.Mobile ||
        messageState.status === 'loading' ||
        messageState.messages.length === 0 ? null : (
        <StNoMessageSelectedContainer>
          <StNoMessageSelectedImage src='/img/no-item-selected.png' alt='No message selected' />
          <H3 style={{ fontSize: '14px' }}>
            {t({
              id: 'message-detail.noMessageSelected',
              message: 'Selecteer een bericht links in de lijst',
            })}
          </H3>
        </StNoMessageSelectedContainer>
      )}
    </StContentGrid>
  )
}

const StContentGrid = styled(ContentGrid)`
  width: 100%;
  height: 100%;

  background-color: ${({ theme }) => theme.Colors.Background.White};

  @media ${breakpoints.toM} {
    padding-top: ${shouldShowBanner() ? 20 : 0}px;
  }
`

//  ------------------------------------------------  Desktop content ------------------------------------------------ /

const StDesktopMessageList = styled(FlexColumn)`
  width: 100%;
  height: 100%;
  border-right: 1px solid ${({ theme }) => theme.Colors.Neutrals.Light};
  flex: 1;
`

const StWebTabbar = styled(TabBar)`
  width: 100%;
  border-bottom: 1px solid ${({ theme }) => theme.Colors.Neutrals.Light};
  padding: 0 ${({ theme }) => theme.UI.SpacingPx.Space5};
  display: sticky;
  top: 0;

  @media ${breakpoints.s} {
    padding: 0 calc(${({ theme }) => theme.UI.SpacingPx.Space6} - 16px);
  }
  @media ${breakpoints.m} {
    padding: 0 calc(${({ theme }) => theme.UI.SpacingPx.Space12} - 16px);
  }

  @media ${breakpoints.l} {
    padding: 0 calc(${({ theme }) => theme.UI.SpacingPx.Space20} - 16px);
  }
`

const StNoMessageSelectedContainer = styled(FlexColumn)`
  flex: 1;
  justify-content: center;
  align-items: center;
  height: 100%;

  display: none;

  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};
`

const StNoMessageSelectedImage = styled.img`
  max-width: 120px;
  width: 50%;
  opacity: 0.5;
`

//  ------------------------------------------------  Mobile components ------------------------------------------------ /

const StMobileMessageList = styled(FlexColumn)`
  width: 100%;
  height: 100%;
  flex: 1;
`

const StMobileHeaderContainer = styled(FlexColumn)`
  width: 100%;
  position: sticky;
  padding: ${({ theme }) => theme.UI.SpacingPx.Space2}
    ${({ theme }) => theme.UI.SpacingPx.Space5};
  padding-bottom: 0;
`
